import styled from 'styled-components';
import { CSSVars } from '@/styles/styles.config';

export const StyledContentWrapper = styled.div`
  background: ${CSSVars.white};
`;

export const StyledSectionLabel = styled.p`
  font-size: ${CSSVars.fontSizeXS};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: ${CSSVars.blue500};
  margin-bottom: 14px;
`;

export const StyledSectionHeading = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${CSSVars.black};
  margin-bottom: 18px;
  line-height: 1.25;

  @media screen and (min-width: ${CSSVars.breakpointMD}) { font-size: ${CSSVars.fontSize2XL}; }
  @media screen and (min-width: ${CSSVars.breakpointLG}) { font-size: ${CSSVars.fontSize3XL}; }
`;

export const StyledSectionIntro = styled.p`
  font-size: ${CSSVars.fontSizeMD};
  color: ${CSSVars.gray600};
  line-height: 1.75;
  max-width: 680px;
  font-weight: 300;
`;

export const StyledSection = styled.section<{ bg?: string }>`
  padding: 60px 20px;
  background: ${({ bg }) => bg || CSSVars.white};
  border-top: 1px solid rgba(0, 0, 0, 0.07);

  @media screen and (min-width: ${CSSVars.breakpointMD}) { padding: 80px 30px; }
  @media screen and (min-width: ${CSSVars.breakpointLG}) { padding: 110px 40px; }
`;

export const StyledInner = styled.div<{ maxWidth?: string }>`
  max-width: ${({ maxWidth }) => maxWidth || '960px'};
  margin: 0 auto;
`;

export const StyledStepsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin-top: 52px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) { grid-template-columns: repeat(3, 1fr); }
`;

export const StyledStep = styled.div`
  background: ${CSSVars.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 18px;
  padding: 34px 28px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;

  &:hover { border-color: rgba(76, 79, 255, 0.3); box-shadow: 0 6px 24px rgba(76, 79, 255, 0.1); transform: translateY(-2px); }

  .step-number { display: inline-flex; align-items: center; justify-content: center; width: 38px; height: 38px; background: ${CSSVars.blue500}; color: ${CSSVars.white}; font-size: ${CSSVars.fontSizeSM}; font-weight: 700; border-radius: 10px; margin-bottom: 22px; }
  h3 { font-size: ${CSSVars.fontSizeLG}; font-weight: 700; color: ${CSSVars.black}; margin-bottom: 12px; line-height: 1.3; }
  p { font-size: ${CSSVars.fontSizeSM}; color: ${CSSVars.gray600}; line-height: 1.75; font-weight: 300; }
`;

export const StyledFormulaBox = styled.div`
  background: linear-gradient(135deg, #09093a 0%, #0d0e20 100%);
  border: 1px solid rgba(76, 79, 255, 0.3);
  border-radius: 16px;
  padding: 32px 36px;
  margin: 36px 0 16px;

  .label { font-size: ${CSSVars.fontSizeXS}; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: ${CSSVars.gray400}; margin-bottom: 14px; }
  .formula { font-size: ${CSSVars.fontSizeLG}; font-weight: 700; color: ${CSSVars.white}; line-height: 1.5;
    @media screen and (min-width: ${CSSVars.breakpointMD}) { font-size: ${CSSVars.fontSizeXL}; }
  }
  .example { margin-top: 16px; font-size: ${CSSVars.fontSizeSM}; color: ${CSSVars.gray300}; line-height: 1.6;
    span { color: ${CSSVars.pink300}; font-weight: 700; }
  }
`;

export const StyledFormulaBody = styled.div`
  p { font-size: ${CSSVars.fontSizeMD}; color: ${CSSVars.gray600}; line-height: 1.85; margin-top: 22px; font-weight: 300; }
`;

export const StyledDataTable = styled.div`
  margin-top: 44px;
  overflow-x: auto;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.1);

  table { width: 100%; border-collapse: collapse; font-size: ${CSSVars.fontSizeSM}; min-width: 500px; }
  thead tr { background: linear-gradient(90deg, #09093a 0%, #0d0e30 100%); }
  thead th { padding: 16px 20px; text-align: left; font-weight: 700; color: ${CSSVars.gray300}; text-transform: uppercase; letter-spacing: 0.1em; font-size: ${CSSVars.fontSizeXS}; }
  tbody tr { background: ${CSSVars.white}; border-bottom: 1px solid rgba(0, 0, 0, 0.07); transition: background 0.15s;
    &:last-child { border-bottom: none; }
    &:hover { background: rgba(76, 79, 255, 0.04); }
  }
  tbody td { padding: 14px 20px; color: ${CSSVars.gray700}; line-height: 1.55; vertical-align: middle; }
  .col-value { font-weight: 700; color: ${CSSVars.blue500}; }
  .col-note { color: ${CSSVars.gray500}; font-size: ${CSSVars.fontSizeXS}; line-height: 1.65; font-weight: 300; }
`;

export const StyledBadge = styled.span<{ color: string; bg: string }>`
  display: inline-block;
  padding: 3px 12px;
  border-radius: 20px;
  font-size: ${CSSVars.fontSizeXS};
  font-weight: 700;
  color: ${({ color }) => color};
  background: ${({ bg }) => bg};
`;

export const StyledTableFootnote = styled.p`
  font-size: ${CSSVars.fontSizeXS};
  color: ${CSSVars.gray500};
  margin-top: 18px;
  line-height: 1.65;
`;

export const StyledFactorsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-top: 52px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) { grid-template-columns: repeat(2, 1fr); }
  @media screen and (min-width: ${CSSVars.breakpointLG}) { grid-template-columns: repeat(3, 1fr); }
`;

export const StyledFactorCard = styled.div`
  background: ${CSSVars.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 18px;
  padding: 30px 26px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;

  &:hover { border-color: rgba(76, 79, 255, 0.3); box-shadow: 0 6px 24px rgba(76, 79, 255, 0.1); transform: translateY(-2px); }

  .factor-icon { font-size: 30px; margin-bottom: 18px; display: block; }
  h3 { font-size: ${CSSVars.fontSizeMD}; font-weight: 700; color: ${CSSVars.black}; margin-bottom: 10px; line-height: 1.3; }
  p { font-size: ${CSSVars.fontSizeSM}; color: ${CSSVars.gray600}; line-height: 1.75; font-weight: 300; }
  .factor-stat { display: inline-block; margin-top: 16px; padding: 5px 14px; border-radius: 8px; background: rgba(76, 79, 255, 0.08); border: 1px solid rgba(76, 79, 255, 0.2); color: ${CSSVars.blue500}; font-size: ${CSSVars.fontSizeXS}; font-weight: 700; }
`;

export const StyledTipsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-top: 52px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) { grid-template-columns: repeat(2, 1fr); }
`;

export const StyledTipCard = styled.div`
  background: ${CSSVars.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 18px;
  padding: 30px 26px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;

  &:hover { border-color: rgba(76, 79, 255, 0.3); box-shadow: 0 6px 24px rgba(76, 79, 255, 0.1); transform: translateY(-2px); }

  .tip-header { display: flex; align-items: flex-start; gap: 14px; }
  .tip-number { flex-shrink: 0; width: 34px; height: 34px; border-radius: 10px; background: ${CSSVars.blue500}; color: ${CSSVars.white}; font-size: ${CSSVars.fontSizeXS}; font-weight: 700; display: flex; align-items: center; justify-content: center; }
  h3 { font-size: ${CSSVars.fontSizeMD}; font-weight: 700; color: ${CSSVars.black}; line-height: 1.3; padding-top: 5px; }
  p { font-size: ${CSSVars.fontSizeSM}; color: ${CSSVars.gray600}; line-height: 1.75; font-weight: 300; }
  a.tip-cta { display: inline-block; margin-top: 6px; font-size: ${CSSVars.fontSizeSM}; font-weight: 700; color: ${CSSVars.blue500}; text-decoration: none; transition: color 0.2s; &:hover { color: ${CSSVars.pink300}; } }
`;

export const StyledMetricsTable = styled.div`
  margin-top: 44px;
  overflow-x: auto;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.1);

  table { width: 100%; border-collapse: collapse; font-size: ${CSSVars.fontSizeSM}; min-width: 580px; }
  thead tr { background: linear-gradient(90deg, #09093a 0%, #0d0e30 100%); }
  thead th { padding: 16px 20px; text-align: left; font-weight: 700; color: ${CSSVars.gray300}; text-transform: uppercase; letter-spacing: 0.1em; font-size: ${CSSVars.fontSizeXS}; }
  tbody tr { background: ${CSSVars.white}; border-bottom: 1px solid rgba(0, 0, 0, 0.07); &:last-child { border-bottom: none; } &:hover { background: rgba(76, 79, 255, 0.04); } }
  tbody td { padding: 14px 20px; color: ${CSSVars.gray700}; line-height: 1.65; vertical-align: top; font-weight: 300; }
  .metric-name { font-weight: 700; color: ${CSSVars.black}; white-space: nowrap; }
  .metric-formula { font-family: monospace; background: rgba(76, 79, 255, 0.1); border: 1px solid rgba(76, 79, 255, 0.25); padding: 2px 10px; border-radius: 6px; font-size: ${CSSVars.fontSizeXS}; color: ${CSSVars.blue500}; white-space: nowrap; }
`;

export const StyledRelatedGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-top: 52px;

  @media screen and (min-width: ${CSSVars.breakpointMD}) { grid-template-columns: repeat(2, 1fr); }
  @media screen and (min-width: ${CSSVars.breakpointLG}) { grid-template-columns: repeat(3, 1fr); }
`;

export const StyledRelatedCard = styled.a`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 28px 24px;
  background: ${CSSVars.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  text-decoration: none;
  color: inherit;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;

  &:hover { border-color: rgba(76, 79, 255, 0.3); box-shadow: 0 6px 24px rgba(76, 79, 255, 0.1); transform: translateY(-3px); }

  h3 { font-size: ${CSSVars.fontSizeMD}; font-weight: 700; color: ${CSSVars.black}; line-height: 1.4; }
  p { font-size: ${CSSVars.fontSizeSM}; color: ${CSSVars.gray600}; line-height: 1.7; flex: 1; font-weight: 300; }
  .read-more { font-size: ${CSSVars.fontSizeSM}; font-weight: 700; color: ${CSSVars.blue500}; margin-top: 6px; transition: color 0.2s; }
  &:hover .read-more { color: ${CSSVars.pink300}; }
`;
